import { CopilotQuestionOptionFilter } from './copilotQuestionOption';

// The typed questions an agent asks when a request is too vague to act on (phase 32,
// docs/phase-32-drafting-questions.md in skillcontrol-api). The shapes mirror the QuestionSet block's payload as the
// automation drafting process sends it (AutomationQuestionState without the captured call), and the answers json the
// card writes back is SkillControl's QuestionAnswers wire shape, passed to the drafter verbatim.
export interface AutomationQuestionOption {
  id?: string;
  label: string;
  description?: string;
  // a candidate card of the general knowledge loop: the record the option names, set on every option or on none
  recordType?: string;
  recordId?: string;
  // a preset of a date or dateRange question, from the server: its relative value (+14d, endOfMonth,
  // today..+30d) and the ISO dates (yyyy-MM-dd) the value resolved to when the card was built
  value?: string;
  date?: string;
  from?: string;
  to?: string;
  // the server's preselected option of a single question (the KPI report's format: PDF); the user can change it
  selected?: boolean;
}

export interface AutomationQuestion {
  id?: string;
  // a chip of a few words naming the subject, such as Source or Outcome
  header?: string;
  text: string;
  // single (pick one option), multi (pick any) or text (free text), as SkillControl sends it; lookup (pick
  // from the tenant's own rows, loaded from copilot/questions/options), date (one day) or dateRange (a period),
  // as the general knowledge loop sends it
  kind?: string;
  options?: AutomationQuestionOption[];
  allowOther?: boolean;
  required?: boolean;
  // lookup only: several rows may be picked
  multi?: boolean;
  // lookup only: the row source (TaskTemplates, Tags, Users, Risks, ...), matched case-insensitively by the server
  source?: string;
  // lookup only: the narrowing the server applies to the source
  filter?: CopilotQuestionOptionFilter;
  // lookup only: the most rows one search returns (1..200)
  max?: number;
}

export interface AutomationQuestionSetPayload {
  setId?: string;
  reply?: string;
  questions?: AutomationQuestion[];
}

// One answer as the json carries it: the question text and the chosen labels are echoed, so the exchange stands alone
export interface AutomationQuestionAnswer {
  questionId?: string;
  question: string;
  selectedLabels: string[];
  // the ids of the chosen rows (a lookup) or the recordIds of the chosen options (a candidate card), verbatim and
  // in the order of selectedLabels; an empty entry is an option without a record
  selectedIds?: string[];
  text?: string;
}

// The longest free-text answer of one question; the server bounds the whole answers json too
export const questionAnswerTextMaxLength = 1000;

// the input names the card writes onto its block; the server reads them back (AutomationDraftingStep)
export const questionAnswersInput = 'answers';
export const questionSetIdInput = 'setId';
// set by the server on the card a typed message answered (GeneralKnowledgeAgentManager.AnsweredInChatInput)
export const questionAnsweredInChatInput = 'answeredInChat';

export const parseQuestionSetPayload = (payload: unknown): AutomationQuestionSetPayload | undefined => {
  if (!payload || typeof payload !== 'object') return undefined;

  const set = payload as AutomationQuestionSetPayload;
  const questions = Array.isArray(set.questions)
    ? set.questions.filter((question) => question && typeof question.text === 'string' && question.text.trim() !== '')
    : [];
  if (questions.length === 0) return undefined;

  return { setId: set.setId, reply: set.reply, questions };
};

export const parseQuestionAnswersJson = (json: string | undefined): AutomationQuestionAnswer[] | undefined => {
  if (!json) return undefined;

  try {
    const parsed = JSON.parse(json) as { answers?: AutomationQuestionAnswer[] };
    if (!Array.isArray(parsed.answers)) return undefined;

    //the json round-trips through server state: every entry is normalized so a reader never meets a missing list
    return parsed.answers
      .filter((answer) => answer && typeof answer === 'object')
      .map((answer) => ({
        questionId: answer.questionId,
        question: typeof answer.question === 'string' ? answer.question : '',
        selectedLabels: Array.isArray(answer.selectedLabels) ? answer.selectedLabels : [],
        selectedIds: Array.isArray(answer.selectedIds) ? answer.selectedIds.map(String) : undefined,
        text: typeof answer.text === 'string' ? answer.text : undefined,
      }));
  } catch {
    return undefined;
  }
};

export const buildQuestionAnswersJson = (setId: string | undefined, answers: AutomationQuestionAnswer[]): string => {
  return JSON.stringify({ setId: setId ?? '', answers });
};

// a chosen label with its row id, "Incident report (42)", so the model can pass the id to a simulate
const describeSelection = (answer: AutomationQuestionAnswer): string =>
  answer.selectedLabels
    .map((label, index) => {
      const id = answer.selectedIds?.[index];

      return id ? `${label} (${id})` : label;
    })
    .join(', ');

// the readable lines of the ANSWERED questions, for the user bubble (the same shape SkillControl echoes into its
// transcript); a skipped optional question adds no line
export const describeQuestionAnswers = (answers: AutomationQuestionAnswer[]): string => {
  return answers
    .filter((answer) => answer.selectedLabels.length > 0 || answer.text)
    .map((answer) => `${answer.question} ${[describeSelection(answer), answer.text].filter(Boolean).join('; ')}`.trim())
    .join('\n');
};
